// ChildRegistration.jsx
import React, { useState } from 'react';
import { useChild } from './hooks/useChild';

const ChildRegistration = ({ childData, mode, childId }) => {
  const { verifyRegistration, setRegistrationManual, downloadFile } = useChild(childId);
  
  const [registrationComment, setRegistrationComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerifyRegistration = async () => {
    try {
      setLoading(true);
      await verifyRegistration(registrationComment);
    } catch (error) {
      console.error('Error verifying registration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetRegistrationManual = async () => {
    try {
      setLoading(true);
      await setRegistrationManual(registrationComment);
    } catch (error) {
      console.error('Error setting registration manually:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (document) => {
    try {
      await downloadFile(childData.id, document.id, "registration", document.originalName);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const getDocumentIcon = (type) => {
    const icons = {
      pdf: '📄',
      jpg: '🖼️',
      png: '🖼️',
      zip: '📦',
      docx: '📝',
      xlsx: '📊'
    };
    return icons[type] || '📎';
  };

  // Проверяем роль пользователя (в реальном приложении это будет из контекста/стора)
  const userRole = 'SU'; // или другая роль

  return (
    <div className="registration-section">
      <h3 className="section-title">Проживання та реєстрація</h3>
      
      <div className="data-section">
        <div className="data-row">
          <label className="data-label">Адреса фактичного проживання:</label>
          <span className="data-value">{childData.actualAddress}</span>
        </div>
        
        <div className="data-row">
          <label className="data-label">Адреса місця реєстрації:</label>
          <span className="data-value">{childData.registeredAddress}</span>
        </div>
        
        <div className="data-row">
          <label className="data-label">Статус підтвердження РТГК:</label>
          <div className="status-section">
            <span className={`status-badge ${
              childData.registrationStatus === 'verified' 
                ? 'status-verified' 
                : 'status-pending'
            }`}>
              {childData.registrationStatus === 'verified' 
                ? 'Підтверджено' 
                : 'Не підтверджено'}
            </span>
            <span className="status-text">
              {childData.registrationStatus === 'verified' 
                ? 'Реєстрація підтверджена в РТГК' 
                : 'Реєстрація не підтверджена в РТГК'}
            </span>
          </div>
        </div>

        {childData.registrationVerifiedBy && (
          <div className="data-row">
            <label className="data-label">Підтверджено користувачем:</label>
            <span className="data-value">{childData.registrationVerifiedBy}</span>
          </div>
        )}

        {childData.registrationVerifiedAt && (
          <div className="data-row">
            <label className="data-label">Дата підтвердження:</label>
            <span className="data-value">
              {new Date(childData.registrationVerifiedAt).toLocaleDateString('uk-UA')}
            </span>
          </div>
        )}
      </div>

      {/* Документы для регистрации */}
      {childData.registrationDocuments && childData.registrationDocuments.length > 0 && (
        <div className="documents-section">
          <h4 className="documents-title">Документи для реєстрації</h4>
          <div className="documents-list">
            {childData.registrationDocuments.map(doc => (
              <div key={doc.id} className="document-item">
                <div className="document-info">
                  <span className="document-icon">{getDocumentIcon(doc.type)}</span>
                  <div className="document-details">
                    <div className="document-name">{doc.name}</div>
                    <div className="document-meta">
                      <span className="document-type">{doc.type}</span>
                      <span className="document-size">{doc.size}</span>
                      <span className="document-date">{doc.uploadedAt}</span>
                    </div>
                  </div>
                </div>
                <button 
                  className="btn-download"
                  onClick={() => handleDownload(doc)}
                  title="Скачати документ"
                  disabled={loading}
                >
                  ⬇️
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Действия для разных ролей */}
      {mode === 'edit' && (
        <div className="actions-section">
          <div className="comment-field">
            <label htmlFor="registration-comment" className="comment-label">
              Коментар до реєстрації:
            </label>
            <textarea
              id="registration-comment"
              className="form-control comment-textarea"
              value={registrationComment}
              onChange={(e) => setRegistrationComment(e.target.value)}
              placeholder="Введіть коментар до реєстрації..."
              rows="3"
            />
          </div>
          
          <div className="action-buttons">
            {userRole === 'SU' ? (
              <button 
                className="btn btn-success"
                onClick={handleSetRegistrationManual}
                disabled={childData.registrationStatus === 'verified' || loading}
              >
                {loading ? 'Обробка...' : 'Встановити реєстрацію вручну'}
              </button>
            ) : (
              <button 
                className="btn btn-primary"
                onClick={handleVerifyRegistration}
                disabled={childData.registrationStatus === 'verified' || loading}
              >
                {loading ? 'Обробка...' : 'Перевірити реєстрацію'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildRegistration;